import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Customer } from '../../types/customer';
import { SimpleOrganization } from '../../pages/Customers';
import { BuildingOffice2Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface BulkOrganizationAssignmentProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCustomers: Customer[];
  organizations: SimpleOrganization[];
  onAssign: (customerIds: string[], organizationIds: string[]) => Promise<void>;
}

const BulkOrganizationAssignment: React.FC<BulkOrganizationAssignmentProps> = ({
  isOpen,
  onClose,
  selectedCustomers,
  organizations,
  onAssign
}) => {
  const { t } = useLanguage();
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleOrg = (orgId: string) => {
    setSelectedOrgIds(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const handleSubmit = async () => {
    if (selectedOrgIds.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const customerIds = selectedCustomers.map(c => c.id);
      await onAssign(customerIds, selectedOrgIds);
      setSelectedOrgIds([]);
      onClose();
    } catch (error) {
      console.error('Error assigning organizations:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedOrgIds([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Tömeges szervezet hozzárendelés"
      size="lg"
    >
      <div className="space-y-6">
        {/* Selected customers summary */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            Kiválasztott ügyfelek ({selectedCustomers.length})
          </h4>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {selectedCustomers.map(customer => (
              <div key={customer.id} className="text-sm text-blue-700">
                • {customer.companyName}
              </div>
            ))}
          </div>
        </div>

        {/* Organization selection */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Válassz szervezetet(eke)t
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {organizations.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Nincs elérhető szervezet
              </p>
            ) : (
              organizations.map(org => (
                <label
                  key={org.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={selectedOrgIds.includes(org.id)}
                    onChange={() => handleToggleOrg(org.id)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <BuildingOffice2Icon className="w-5 h-5 text-gray-400 ml-3 mr-2" />
                  <span className="text-sm text-gray-900">{org.name}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Selected organizations summary */}
        {selectedOrgIds.length > 0 && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="text-sm font-medium text-green-900 mb-2">
              Kiválasztott szervezetek ({selectedOrgIds.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {selectedOrgIds.map(orgId => {
                const org = organizations.find(o => o.id === orgId);
                return org ? (
                  <span
                    key={orgId}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                  >
                    {org.name}
                    <button
                      onClick={() => handleToggleOrg(orgId)}
                      className="ml-2 hover:text-green-900"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Mégse
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedOrgIds.length === 0 || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Hozzárendelés...' : `Hozzárendelés (${selectedCustomers.length} ügyfél)`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkOrganizationAssignment;
